import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { optimizeTour } from '../services/routeService';
import VehicleRouteMap from './VehicleRouteMap';

const VEHICLE_COLORS = [
  '#FF1744', '#00E676', '#2979FF', '#FF9100', '#E91E63',
  '#00BCD4', '#FFC107', '#9C27B0', '#4CAF50', '#FF5722',
];

function parseDuration(str) {
  if (!str) return 0;
  const match = str.match(/([\d.]+)s/);
  return match ? parseFloat(match[1]) : 0;
}

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function formatTime(isoString) {
  if (!isoString) return '-';
  try {
    const match = isoString.match(/T(\d{2}):(\d{2})/);
    if (!match) return isoString;
    let h = parseInt(match[1], 10);
    const m = match[2];
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${String(h).padStart(2, '0')}:${m} ${ampm}`;
  } catch {
    return isoString;
  }
}

function downloadSampleEmployeeExcel() {
  const data = [
    { employeeId: 1057572, lat: 12.89, lng: 77.5691, 'Office ShiftTime': '9:30 AM' },
    { employeeId: 1057518, lat: 12.8792, lng: 77.596, 'Office ShiftTime': '11:30 AM' },
    { employeeId: 1056989, lat: 12.97, lng: 77.7068, 'Office ShiftTime': '11:30 AM' },
    { employeeId: 1057366, lat: 12.9342, lng: 77.5635, 'Office ShiftTime': '9:30 AM' },
    { employeeId: 1057365, lat: 12.9413, lng: 77.6178, 'Office ShiftTime': '11:30 AM' },
    { employeeId: 1057367, lat: 12.9423, lng: 77.5447, 'Office ShiftTime': '11:30 AM' },
    { employeeId: 1056991, lat: 12.9733, lng: 77.7103, 'Office ShiftTime': '9:30 AM' },
  ];
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Employees');
  XLSX.writeFile(wb, 'employee_sample.xlsx');
}

function downloadSampleVehicleExcel() {
  const data = [
    { Name: 1, Capacity: 6 },
    { Name: 2, Capacity: 12 },
    { Name: 3, Capacity: 4 },
    { Name: 4, Capacity: 4 },
  ];
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Vehicles');
  XLSX.writeFile(wb, 'vehicle_sample.xlsx');
}

function GoogleView() {
  const [employeeFile, setEmployeeFile] = useState(null);
  const [vehicleFile, setVehicleFile] = useState(null);
  const [officeLocation, setOfficeLocation] = useState({ lat: 13.07091831154783, lng: 77.59197669595824 });
  const [date, setDate] = useState('2026-03-13');
  const [googleProjectId, setGoogleProjectId] = useState('gold-pod-392405');
  const [authToken, setAuthToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleFileChange = (e, setter) => {
    const f = e.target.files[0];
    if (f) {
      if (
        f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        f.type === 'application/vnd.ms-excel'
      ) {
        setter(f);
        setError('');
      } else {
        setError('Please upload a valid Excel file (.xlsx or .xls)');
      }
    }
  };

  const handleSubmit = async () => {
    if (!employeeFile) { setError('Please upload employee file'); return; }
    if (!vehicleFile) { setError('Please upload vehicle file'); return; }
    if (!authToken.trim()) { setError('Auth token is required'); return; }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await optimizeTour({
        file: employeeFile,
        vehicleFile,
        officeLocation,
        date,
        googleProjectId,
        authToken: authToken.trim(),
      });

      if (response.errorCode === 200) {
        setResult(response.responseData);
        console.log('Optimize tour response:', response.responseData);
      } else {
        throw new Error(response.errorMessage || 'Failed to optimize tour');
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setEmployeeFile(null);
    setVehicleFile(null);
    setResult(null);
    setError('');
  };

  return (
    <>
      <div className="upload-section">
        <div className="card">
          <h2>Google Route Optimization</h2>

          {/* Office Location */}
          <div className="destination-input">
            <h3>Office Location</h3>
            <div className="input-group">
              <div className="input-field">
                <label>Latitude:</label>
                <input
                  type="number" step="0.000001" value={officeLocation.lat}
                  onChange={(e) => setOfficeLocation({ ...officeLocation, lat: parseFloat(e.target.value) })}
                />
              </div>
              <div className="input-field">
                <label>Longitude:</label>
                <input
                  type="number" step="0.000001" value={officeLocation.lng}
                  onChange={(e) => setOfficeLocation({ ...officeLocation, lng: parseFloat(e.target.value) })}
                />
              </div>
            </div>
          </div>

          {/* Date & Project ID */}
          <div className="input-group" style={{ marginBottom: '1.5rem' }}>
            <div className="input-field">
              <label>Date:</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="input-field">
              <label>Google Project ID:</label>
              <input type="text" value={googleProjectId} onChange={(e) => setGoogleProjectId(e.target.value)} />
            </div>
          </div>

          {/* Auth Token */}
          <div className="input-field" style={{ marginBottom: '1.5rem' }}>
            <label>Auth Token:</label>
            <textarea
              rows={3}
              value={authToken}
              onChange={(e) => setAuthToken(e.target.value)}
              placeholder="Paste Google OAuth2 Bearer token here..."
              style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '2px solid #e0e0e0', fontFamily: 'monospace', fontSize: '0.85rem', resize: 'vertical' }}
            />
          </div>

          {/* File Uploads */}
          <div className="input-group" style={{ marginBottom: '1.5rem' }}>
            <div>
              <label style={{ fontWeight: 600, color: '#555', display: 'block', marginBottom: '0.5rem' }}>Employee File:</label>
              <div className="file-upload">
                <input type="file" accept=".xlsx,.xls" onChange={(e) => handleFileChange(e, setEmployeeFile)} id="file-input-emp" disabled={loading} />
                <label htmlFor="file-input-emp" className="file-label">
                  {employeeFile ? employeeFile.name : 'Choose Employee Excel'}
                </label>
              </div>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#555', display: 'block', marginBottom: '0.5rem' }}>Vehicle File:</label>
              <div className="file-upload">
                <input type="file" accept=".xlsx,.xls" onChange={(e) => handleFileChange(e, setVehicleFile)} id="file-input-veh" disabled={loading} />
                <label htmlFor="file-input-veh" className="file-label">
                  {vehicleFile ? vehicleFile.name : 'Choose Vehicle Excel'}
                </label>
              </div>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="button-group">
            <button onClick={handleSubmit} disabled={loading || !employeeFile || !vehicleFile} className="btn btn-primary">
              {loading ? 'Optimizing...' : 'Optimize Tour'}
            </button>
            <button onClick={downloadSampleEmployeeExcel} className="btn btn-secondary">
              Sample Employee Excel
            </button>
            <button onClick={downloadSampleVehicleExcel} className="btn btn-secondary">
              Sample Vehicle Excel
            </button>
            {result && (
              <button onClick={handleReset} className="btn btn-danger">Reset</button>
            )}
          </div>
        </div>

        {loading && (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Optimizing tour (this may take up to 2 minutes)...</p>
          </div>
        )}
      </div>

      {/* Validation Errors */}
      {result && result.validationErrors && result.validationErrors.length > 0 && (
        <div className="card">
          <h2>Validation Errors</h2>
          {result.validationErrors.map((err, i) => (
            <div key={i} className="error-message">
              {err.errorMessage || err.displayName || 'Unknown error'}
            </div>
          ))}
        </div>
      )}

      {/* Metrics Summary */}
      {result && result.metrics && (
        <div className="card">
          <h2>Optimization Summary</h2>
          <div className="summary">
            <div className="summary-item">
              <span className="label">Vehicles Used</span>
              <span className="value">{result.metrics.usedVehicleCount ?? '-'}</span>
            </div>
            <div className="summary-item">
              <span className="label">Shipments</span>
              <span className="value">{result.metrics.aggregatedRouteMetrics?.performedShipmentCount ?? '-'}</span>
            </div>
            <div className="summary-item">
              <span className="label">Total Travel</span>
              <span className="value">
                {result.metrics.aggregatedRouteMetrics?.travelDistanceMeters
                  ? (result.metrics.aggregatedRouteMetrics.travelDistanceMeters / 1000).toFixed(1) + ' km'
                  : '-'}
              </span>
            </div>
            <div className="summary-item">
              <span className="label">Travel Duration</span>
              <span className="value">
                {formatDuration(parseDuration(result.metrics.aggregatedRouteMetrics?.travelDuration))}
              </span>
            </div>
            <div className="summary-item">
              <span className="label">Earliest Start</span>
              <span className="value">{formatTime(result.metrics.earliestVehicleStartTime)}</span>
            </div>
            <div className="summary-item">
              <span className="label">Latest End</span>
              <span className="value">{formatTime(result.metrics.latestVehicleEndTime)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Routes */}
      {result && result.routes && result.routes.length > 0 && (
        <div className="google-routes">
          <h2 style={{ color: 'white', marginBottom: '1rem' }}>Vehicle Routes</h2>
          {result.routes.map((route, idx) => {
            const color = VEHICLE_COLORS[idx % VEHICLE_COLORS.length];
            const travelSec = parseDuration(route.metrics?.travelDuration);
            const distKm = route.metrics?.travelDistanceMeters
              ? (route.metrics.travelDistanceMeters / 1000).toFixed(1)
              : '-';

            return (
              <div key={idx} className="card vehicle-route-card" style={{ borderLeft: `5px solid ${color}` }}>
                <div className="vehicle-header">
                  <h3 style={{ color }}>
                    Vehicle {route.vehicleLabel || route.vehicleIndex}
                    {route.vehicleCapacity != null && (
                      <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#555', marginLeft: '0.75rem', background: '#f0f2ff', padding: '2px 10px', borderRadius: '12px' }}>
                        Capacity: {route.vehicleCapacity}
                      </span>
                    )}
                  </h3>
                  <div className="vehicle-meta">
                    <span>{formatTime(route.vehicleStartTime)} → {formatTime(route.vehicleEndTime)}</span>
                    <span>{distKm} km</span>
                    <span>{formatDuration(travelSec)} travel</span>
                    <span>{route.metrics?.performedShipmentCount ?? 0} shipments</span>
                    {route.vehicleCapacity != null && (
                      <span>{route.metrics?.maxLoads?.people?.amount ?? 0} / {route.vehicleCapacity} seats used</span>
                    )}
                  </div>
                </div>

                <VehicleRouteMap
                  visits={route.visits}
                  color={color}
                  officeLocation={officeLocation}
                />

                <div className="visits-table-wrapper">
                  <table className="visits-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Type</th>
                        <th>Employee</th>
                        <th>Location</th>
                        <th>Time</th>
                        <th>Load</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(route.visits || []).map((visit, vi) => (
                        <tr key={vi} className={visit.isPickup ? 'visit-pickup' : 'visit-delivery'}>
                          <td>{vi + 1}</td>
                          <td>
                            <span className={`visit-badge ${visit.isPickup ? 'badge-pickup' : 'badge-delivery'}`}>
                              {visit.isPickup ? 'Pickup' : 'Drop'}
                            </span>
                          </td>
                          <td>{visit.shipmentLabel || `Shipment ${visit.shipmentIndex}`}</td>
                          <td className="location-cell">
                            {visit.lat != null && visit.lng != null
                              ? `${visit.lat.toFixed(4)}, ${visit.lng.toFixed(4)}`
                              : '-'}
                          </td>
                          <td>{formatTime(visit.startTime)}</td>
                          <td>{visit.loadDemands?.people?.amount ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

export default GoogleView;
